import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import arrowDownIcon from 'assets/images/icons/arrow-down.svg';
import formGreenIcon from 'assets/images/icons/form-green.svg';
import AddCommentModal from './comment/AddCommentModal';
import AddSubmission from './AddSubmission';
import Pagination from 'components/common-components/Pagination';
import ReportModal from 'pages/report/ReportModal';
import SelectField from 'components/form-components/SelectField';
import SubmissionFilters from './SubmissionFilters';
import SubmissionLogs from './SubmissionLogs';
import SupervisorLockedModal from './SupervisorLockedModal';
import TableComponent from 'components/table-component/Table';
import { CallAPI } from 'actions/General';
import { defaultSupervisorStatusOptionsList } from 'pages/form/utils';
import {
  amendFieldIdentities,
  imageUrlToBase64,
  listManagerSectionPermissions,
  submissionsHeader,
} from './utils';
import { signatureStartingText } from 'config/config';
import { uploadImage } from 'pages/company/utils';

function Submissions() {
  const { lang } = useSelector((state) => state.language);
  const authProfile = useSelector((state) => state.auth.profile);
  const currentUserId = authProfile.userCompanyId;
  const currentUserRole = authProfile.role;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [fieldKeys, setFieldKeys] = useState({});
  const [versionsList, setVersionsList] = useState([]);
  const [signatureMediaIds, setSignatureMediaIds] = useState({});
  const [submissionsList, setSubmissionsList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [submissionHeaderList, setSubmissionHeaderList] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [allQuestionsTitleMap, setAllQuestionsTitleMap] = useState({});
  const [loader, setLoader] = useState(false);
  const [submissionLoader, setSubmissionLoader] = useState(false);
  const [displayEntries, setDisplayEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [addSubmission, setAddSubmission] = useState(false);
  const [supervisorList, setSupervisorList] = useState([]);
  const [sectionSupervisorList, setSectionSupervisorList] = useState([]);
  const [editedSubmissionValues, setEditedSubmissionValues] = useState({});
  const [filterData, setFilterData] = useState({
    formId: '',
    data: '',
    version: '',
    status: '',
    flowState: '',
  });
  const [showAttachment, setShowAttachment] = useState('attachment');
  const [queryFilter, setQueryFilter] = useState({
    searchText: '',
    searchUser: '',
    fromDate: '',
    toDate: '',
    filterSubmission: [],
    filterOperator: '',
  });
  const [searchState, setSearchState] = useState({ searchText: '', searchUser: '' });
  const [formName, setFormName] = useState('');
  const [formDirection, setFormDirection] = useState('');
  const [dataSourceFilters, setDataSourceFilters] = useState([]);
  const [dataSourceFilterData, setDataSourceFilterData] = useState({});
  const [formVersionDetails, setFormVersionDetails] = useState(null);
  const [logsSubmissionId, setLogsSubmissionId] = useState(null);
  const [showSupervisorLockedModal, setShowSupervisorLockedModal] = useState(null);
  const [webReportInfo, setWebReportInfo] = useState(null);
  const [commentsInfo, setCommentsInfo] = useState(null);
  const [webPagePayloadDetails, setWebPagePayloadDetails] = useState({});

  const handleSearchStateChange = (field, value) =>
    setSearchState({ ...searchState, [field]: value });

  const changeDisplayEntries = (name, val) => {
    setCurrentPage(1);
    setDisplayEntries(parseInt(val));
    getFormSubmissions(1, parseInt(val));
  };

  const getBulkImageUrls = async (bulkMediaList) => {
    if (bulkMediaList.length === 0) return {};
    let result = await CallAPI(
      'GET_BULK_DOWNLOAD_MEDIA_REDIRECT_URL',
      { medias: bulkMediaList },
      null,
      null,
      null,
    );
    if (result.status) {
      const mediaUrlMap = Object.fromEntries(result.data.map((media) => [media._id, media.url]));
      return mediaUrlMap;
    } else {
      return {};
    }
  };

  const handleDataSourceFilterDataChange = (name, value) => {
    setDataSourceFilterData({ ...dataSourceFilterData, [name]: value });
  };

  const getFormSubmissions = async (page = 1, limit = 10) => {
    if (filterData.formId) {
      let APICall =
        filterData.data === 'test-data'
          ? 'GET_ALL_TEST_FORM_SUBMISSIONS'
          : filterData.data === 'archive-data'
          ? 'GET_ALL_ARCHIVE_FORM_SUBMISSIONS'
          : 'GET_ALL_FORM_SUBMISSIONS';

      const params = { form: filterData.formId, formVersion: filterData.version, page, limit };

      if (filterData.status !== '') params.filterApprovalStatus = filterData.status;
      if (filterData.flowState !== '' && filterData.flowState !== 'all')
        params.flowState = filterData.flowState;
      if (queryFilter.searchText !== '') params.searchText = queryFilter.searchText.trim();
      if (queryFilter.searchUser !== '') params.userFieldSearch = queryFilter.searchUser.trim();
      if (queryFilter.fromDate !== '') params.fromDate = queryFilter.fromDate;
      if (queryFilter.toDate !== '') params.toDate = queryFilter.toDate;
      if (queryFilter.filterSubmission.length)
        params.filterSubmission = queryFilter.filterSubmission;
      if (queryFilter.filterOperator !== '') params.filterOperator = queryFilter.filterOperator;

      const dataFilterFilledValues = { ...queryFilter.dataSourceFilters };
      for (const key in queryFilter.dataSourceFilters)
        if (dataFilterFilledValues[key] === '') delete dataFilterFilledValues[key];
      if (Object.keys(dataFilterFilledValues).length > 0) {
        params.dataSourceFilters = dataFilterFilledValues;
      } else {
        const allowedDataFiltersPayload = preparePayloadForAllowedFilters(
          formVersionDetails?.dataSourceFilterPermission,
        );
        if (allowedDataFiltersPayload) {
          params.dataSourceId = allowedDataFiltersPayload.dataSourceId;
          params.keyFilterColumnId = allowedDataFiltersPayload.keyFilterColumnId;
          params.filterColumnIds = allowedDataFiltersPayload.filterColumnIds;
        }
      }

      let result = await CallAPI(APICall, params, null, setLoader, null, null);
      if (result.status) {
        let submissionsData = [];

        const records = result.data.submissions ?? result.data.archives ?? [];
        for (let submissionIndex = 0; submissionIndex < records.length; submissionIndex++) {
          const singleSubmission = records[submissionIndex];

          let fieldKeysList = cloneDeep(fieldKeys);
          let singleSubmissionData = {};
          const elementMetaDataMap = {};
          const mediaNotes = {};
          const mediaObj = {};

          for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
            const singleFormEntry = singleSubmission.values[valueIndex];

            let key = singleFormEntry.elementGlobalId;
            let fieldValue = '';
            if (fieldKeysList[key] === 'signature') {
              fieldValue = '';
            } else if (fieldKeysList[key] === 'multiple') {
              fieldValue = singleFormEntry.arrayField;
            } else if (fieldKeysList[key] === 'location') {
              let geoFenceStatusString = '';
              if (singleFormEntry.elementMetaData) {
                const parsedData = JSON.parse(singleFormEntry.elementMetaData);
                if (parsedData?.locationFieldStatus)
                  geoFenceStatusString = `, Geofence Status: ${parsedData.locationFieldStatus}`;
              }
              fieldValue = singleFormEntry.valueField + geoFenceStatusString;
            } else {
              fieldValue = singleFormEntry.valueField;
            }
            if (
              fieldKeysList[key] === 'multiple' ||
              fieldKeysList[key] === 'checkList' ||
              fieldKeysList[key] === 'response'
            ) {
              if (singleFormEntry.elementMetaData) {
                const parsedData = JSON.parse(singleFormEntry.elementMetaData);
                elementMetaDataMap[key] = parsedData.displayMap;
              }
            } else if (fieldKeysList[key] === 'signature') {
              if (singleFormEntry.elementMetaData) {
                const parsedData = JSON.parse(singleFormEntry.elementMetaData);
                elementMetaDataMap[key] = parsedData.signature;
              }
            }
            mediaNotes[key] = singleFormEntry.notesField || '';
            mediaObj[key] = singleFormEntry.media?.map((obj) => obj._id) || [];
            singleSubmissionData[key] = fieldValue;

            if (fieldKeysList[key] !== 'signature') delete fieldKeysList[key];
          }
          Object.keys(fieldKeysList).map((singleNonExistKey) => {
            if (fieldKeysList[singleNonExistKey] !== 'signature') {
              singleSubmissionData[singleNonExistKey] = '';
            }
          });
          let mediaCustomFolderId = singleSubmission.mediaCustomFolderId
            ? singleSubmission.mediaCustomFolderId
            : uuidv4();
          submissionsData.push({
            indexed: (page - 1) * limit + (submissionIndex + 1),
            form_submission_id: singleSubmission._id,
            form_version_id: singleSubmission.formVersion,
            media_custom_folder_id: mediaCustomFolderId,
            submissionCreateByUserId: singleSubmission.user,
            ...singleSubmissionData,
            ...{
              firstName:
                singleSubmission && singleSubmission.userFirstName
                  ? singleSubmission.userFirstName
                  : 'N/A',
              lastName:
                singleSubmission && singleSubmission.userLastName
                  ? singleSubmission.userLastName
                  : 'N/A',
              createdAt: singleSubmission.createdAt,
              createdAtTime: singleSubmission.completeDate,
              referenceNumber: singleSubmission.referenceNumber,
              approvalStatus: singleSubmission.approvalStatus,
              entryStatus: singleSubmission.entryStatus,
              submissionFlowState: singleSubmission.submissionFlowState,
              elementMetaDataMap: elementMetaDataMap,
              mediaNotes: mediaNotes,
              mediaObj: mediaObj,
              hasUnreadComments:
                singleSubmission.commentGroup?.commentGroupUsers.find(
                  (u) => u.companyUserId === currentUserId,
                )?.isMessagesRead === false,
            },
          });
        }

        setSubmissionsList(submissionsData);
        if (page === 1) setTotalRecords(result.data.totalCount);
        attachMedia(cloneDeep(submissionsData), cloneDeep(records));
      }
      setLoader(false);
    }
  };

  const attachMedia = async (submissionData, records) => {
    let submissionDataWithMedia = [];
    let signatureMediaIds = {};
    let fieldKeysList = cloneDeep(fieldKeys);
    const bulkMediaPayload = [];
    for (let submissionIndex = 0; submissionIndex < records.length; submissionIndex++) {
      const singleSubmission = records[submissionIndex];

      for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
        const singleFormEntry = singleSubmission.values[valueIndex];

        if (fieldKeysList[singleFormEntry.elementGlobalId] === 'signature') {
          let media = singleFormEntry.media;
          for (let mediaIndex = 0; mediaIndex < media.length; mediaIndex++) {
            const singleMedia = media[mediaIndex];
            if (
              (singleMedia && singleMedia.imageName.indexOf(signatureStartingText) === 0) ||
              singleFormEntry.elementMetaData?.includes(singleMedia._id)
            ) {
              bulkMediaPayload.push({
                mediaType: 'image',
                imageName: singleMedia.imageName,
                formId: singleSubmission.form,
                versionId: singleSubmission.formVersion,
                customFolderId: singleMedia.customFolderId,
                mediaId: singleMedia._id,
              });
            }
          }
        }
      }
    }
    const mediaUrlMap = await getBulkImageUrls(bulkMediaPayload);
    for (let submissionIndex = 0; submissionIndex < records.length; submissionIndex++) {
      const singleSubmission = records[submissionIndex];
      let singleSubmissionData = {};
      submissionData.map((singleSubmissionRecord) => {
        if (singleSubmission._id === singleSubmissionRecord.form_submission_id)
          singleSubmissionData = singleSubmissionRecord;
      });

      let fieldValue = null;
      for (let valueIndex = 0; valueIndex < singleSubmission.values.length; valueIndex++) {
        const singleFormEntry = singleSubmission.values[valueIndex];

        let key = singleFormEntry.elementGlobalId;

        if (fieldKeysList[key] === 'signature') {
          let media = singleFormEntry.media;
          for (let mediaIndex = 0; mediaIndex < media.length; mediaIndex++) {
            const singleMedia = media[mediaIndex];

            if (
              (singleMedia && singleMedia.imageName.indexOf(signatureStartingText) === 0) ||
              singleFormEntry.elementMetaData?.includes(singleMedia._id)
            ) {
              signatureMediaIds[singleSubmission._id + '_' + key] = singleMedia._id;
              fieldValue = <img className='theme-width-40' src={mediaUrlMap[singleMedia._id]} />;
              singleSubmissionData[key] = fieldValue;
            }
          }
        }
      }

      submissionDataWithMedia.push(singleSubmissionData);
    }
    setSubmissionsList(cloneDeep(submissionDataWithMedia));
    setSignatureMediaIds(cloneDeep(signatureMediaIds));
  };

  const getFormVersionDetail = async () => {
    if (versionsList.length > 0) {
      let apiPath = filterData.formId + '/';
      let formVersion = '';
      if (filterData.version) formVersion += filterData.version;
      else {
        if (filterData.data !== 'test-data') {
          const filteredVersionList = [...versionsList].filter((v) => v.formState === 'published');
          formVersion += filteredVersionList[filteredVersionList.length - 1].value;
        } else {
          formVersion += versionsList[versionsList.length - 1].value;
        }
      }
      if (formVersion) {
        setLoader(true);
        apiPath += formVersion;
        let result = await CallAPI('GET_FORM_VERSION_DETAIL', null, apiPath, null, null, null);
        if (result.status) {
          let fieldKeysList = {};
          let fieldsList = [];
          // If property disabled, remove view logs column
          const headerList = result.data.allowLogs
            ? submissionsHeader
            : submissionsHeader.filter((h) => h.name !== 'viewLogs');
          setSubmissionHeaderList(headerList);

          setFormName(result.data.title);
          setFormDirection(result.data.textDirection);

          setWebPagePayloadDetails((prev) => ({
            ...prev,
            formId: filterData.formId,
            formTitle: result.data.title,
            webPageResponseAllowed: result.data.webPageResponseAllowed,
          }));

          const questionTitlesLookup = {};

          const questionList = [];
          result.data.versions[0].elements.forEach((element) => {
            const properties = JSON.parse(element.properties);
            questionTitlesLookup[element.elementGlobalId] =
              properties.basicProperties.fieldTitle ?? '';

            if (!['page', 'section', 'signature', 'image'].includes(element.elementType)) {
              const options =
                element.elementType === 'checkList' ? properties.basicProperties.optionList : [];
              const type = [
                'text',
                'location',
                'dateTime',
                'qrCode',
                'dataSource',
                'externalApi',
                'sendTo',
              ].includes(element.elementType)
                ? 'string'
                : ['number', 'ratingSlider', 'numberSlider'].includes(element.elementType)
                ? 'number'
                : ['checkList', 'apiStatus'].includes(element.elementType)
                ? properties?.basicProperties?.multipleChoice
                  ? 'multi-choice'
                  : 'single-choice'
                : '';

              questionList.push({
                elementId: element._id,
                elementGlobalId: element.elementGlobalId,
                question: properties.basicProperties.fieldTitle ?? '',
                type,
                options,
              });
            }
          });
          const supervisorQuestionList = [];
          result.data.versions[0].supervisorElements.forEach((element) => {
            const properties = JSON.parse(element.properties);
            questionTitlesLookup[element.elementGlobalId] =
              properties.basicProperties.fieldTitle ?? '';

            if (!['signature', 'section'].includes(element.elementType)) {
              // const options =
              //   element.elementType === 'status' ? properties.basicProperties.optionList : [];
              const options =
                element.elementType === 'status' ? defaultSupervisorStatusOptionsList : [];

              const type = ['note', 'dateTime'].includes(element.elementType)
                ? 'string'
                : ['number', 'ratingSlider'].includes(element.elementType)
                ? 'number'
                : ['response', 'status'].includes(element.elementType)
                ? 'single-choice'
                : '';

              supervisorQuestionList.push({
                elementId: element._id,
                elementGlobalId: element.elementGlobalId,
                question: properties.basicProperties.fieldTitle ?? '',
                type,
                options,
              });
            }
          });
          setAllQuestions([...questionList, ...supervisorQuestionList]);
          setAllQuestionsTitleMap({ ...questionTitlesLookup });

          let supervisorElementsList = result.data.versions[0].supervisorElements ?? [];
          if (result.data.versions[0].statusElement) {
            supervisorElementsList.push(result.data.versions[0].statusElement);
          }
          // first sort data in ascending order w.r.t "position" property in each object
          supervisorElementsList = supervisorElementsList.sort((a, b) => a.position - b.position);
          const allManagerSectionElements = [];

          supervisorElementsList.forEach((ele) => {
            if (ele.elementType === 'section') allManagerSectionElements.push(ele);
            else {
              ele.sectionGlobalId =
                allManagerSectionElements.find(
                  (section) => section.sectionNumber === ele.sectionNumber,
                )?.elementGlobalId ?? allManagerSectionElements[0].elementGlobalId;
            }
          });

          const userMangerSectionPermissions = listManagerSectionPermissions(
            allManagerSectionElements,
            result.data.managerSections ?? [],
            currentUserRole,
            currentUserId,
          );

          let sectionTitle = '';
          let sectionVisibility = {};
          let fieldIdentitiesObj = {};

          supervisorElementsList.map((singleSuperVisorField) => {
            let elementType = singleSuperVisorField.elementType;
            let formSupervisorProperties = JSON.parse(singleSuperVisorField.properties);

            amendFieldIdentities(
              fieldIdentitiesObj,
              singleSuperVisorField,
              formSupervisorProperties.basicProperties.fieldTitle,
            );

            if (elementType === 'section') {
              sectionTitle = formSupervisorProperties?.basicProperties?.sectionTitle;
              sectionVisibility[sectionTitle] =
                formSupervisorProperties.validationProperties.hidden;
            }
            if (elementType !== 'section') {
              // Finding all permissions for this field section
              const foundPermissions = userMangerSectionPermissions.filter(
                (permission) =>
                  permission.managerSectionGlobalId === singleSuperVisorField.sectionGlobalId,
              );
              if (!foundPermissions.length) return;

              // Now check permission by priority of access level
              const priorityPermission =
                foundPermissions.find((permission) => permission.accessLevel === 'full_access') ||
                foundPermissions.find((permission) => permission.accessLevel === 'view_only');

              let additionalDetail = {};
              if (elementType === 'dateTime')
                elementType = formSupervisorProperties.basicProperties.subType;
              if (elementType === 'status') {
                additionalDetail = {
                  ...additionalDetail,
                  optionList: [
                    { responseText: 'Select Option', value: '' },
                    ...formSupervisorProperties.basicProperties.optionList,
                  ],
                };
              }
              if (elementType === 'response') {
                additionalDetail = {
                  ...additionalDetail,
                  optionList: [
                    { responseText: 'Select Option', value: '' },
                    ...formSupervisorProperties.basicProperties.optionList,
                  ],
                };
              }
              if (elementType === 'ratingSlider') {
                additionalDetail = {
                  ...additionalDetail,
                  noOfStars: formSupervisorProperties.basicProperties.ratingPointers,
                };
              }
              let key = '';
              let hintText = '';
              if (formSupervisorProperties?.otherProperties?.shortTitle) {
                key = formSupervisorProperties.otherProperties.shortTitle;
                hintText = formSupervisorProperties.otherProperties.fieldTitle;
              } else key = formSupervisorProperties.basicProperties.fieldTitle;
              let headerSupervisorProp = {
                name: singleSuperVisorField.elementGlobalId,
                isSelected: true,
                displayName: key,
                isFilterAble: false,
                type: elementType,
                associatedKey: singleSuperVisorField.elementGlobalId,
                tableType: 'supervisorFields',
                additionalDetail: additionalDetail,
                hintText,
                sectionTitle,
                hidden:
                  formSupervisorProperties.validationProperties.hidden ||
                  sectionVisibility[sectionTitle],
                fieldIdentitiesObj: fieldIdentitiesObj,
                properties: {
                  mediaProperties: formSupervisorProperties.mediaProperties,
                  validationProperties: formSupervisorProperties.validationProperties,
                },
                ...priorityPermission,
              };
              fieldKeysList[singleSuperVisorField.elementGlobalId] =
                elementType === 'signature'
                  ? 'signature'
                  : elementType === 'response'
                  ? 'response'
                  : elementType === 'date' ||
                    elementType === 'time' ||
                    elementType === 'dateAndTime' ||
                    elementType === 'ratingSlider' ||
                    elementType === 'status'
                  ? ''
                  : '';
              fieldsList.push(headerSupervisorProp);
            }
          });

          setFieldKeys(fieldKeysList);
          setSupervisorList(fieldsList);
          setFormVersionDetails(result.data);
          getUserAllowedFilters(result.data.dataSourceFilterPermission);
        }
      }
    }
  };

  const preparePayloadForAllowedFilters = (dataSourceFilterPermission) => {
    if (dataSourceFilterPermission?.isEnabled) {
      let keyFilterColumnId = null;
      let filterColumnIds = [];

      dataSourceFilterPermission.dataSourceColumns.forEach((column) => {
        if (column.isKeyFilter) keyFilterColumnId = column.columnId;
        else filterColumnIds.push(column.columnId);
      });

      return {
        dataSourceId: dataSourceFilterPermission.dataSourceId,
        keyFilterColumnId,
        filterColumnIds,
      };
    }
  };

  const getUserAllowedFilters = async (dataSourceFilterPermission) => {
    const allowedDataFiltersPayload = preparePayloadForAllowedFilters(dataSourceFilterPermission);
    if (allowedDataFiltersPayload) {
      const response = await CallAPI(
        'GET_USER_ALLOWED_DATA_SOURCE_FILTERS',
        allowedDataFiltersPayload,
        null,
        null,
        null,
        null,
      );
      if (response.status && response.data.dataSourceSchema) {
        const columnMap = Object.fromEntries(
          response.data.dataSourceSchema.map((c) => [c.columnId, c.columnName]),
        );

        const filterList = Object.entries(response.data.dataFilters).map(([columnId, values]) => ({
          name: columnId,
          displayName: columnMap[columnId],
          options: [
            { value: '', displayValue: 'Show All' },
            ...values
              .filter((value) => !!value.name)
              .map((value) => ({ value: value.name, displayValue: value.name })),
          ],
        }));

        setDataSourceFilters(filterList);
      }
    }
  };

  useEffect(() => {
    if (filterData.formId) {
      getFormVersionDetail();
    }
  }, [filterData]);

  useEffect(() => {
    if (formVersionDetails) getFormSubmissions(1, displayEntries);
  }, [submissionHeaderList, queryFilter, formVersionDetails?.versions]);

  useEffect(() => {
    if (Object.keys(dataSourceFilterData).length > 0) {
      setQueryFilter({ ...queryFilter, dataSourceFilters: dataSourceFilterData });
    }
  }, [dataSourceFilterData]);

  useEffect(() => {
    if (location && location.state && location.state.formId) {
      setFilterData({
        ...filterData,
        formId: location && location.state && location.state.formId ? location.state.formId : '',
        version: location && location.state && location.state.version ? location.state.version : '',
        data:
          location && location.state && location.state.status === 'testing'
            ? 'test-data'
            : 'live-data',
      });

      let formVersions =
        location && location.state && location.state.versionsList
          ? location.state.versionsList
          : [];
      const allFormVersions = formVersions.map((singleVersion) => ({
        displayValue: singleVersion.version,
        value: singleVersion._id,
        formState: singleVersion.formState,
      }));
      setVersionsList([
        { displayValue: 'All', value: '', formState: 'published' },
        ...allFormVersions,
      ]);
    } else navigate('/forms');
  }, [location]);

  const onRowsSelection = (name, value) => {
    let selectedIds = cloneDeep(selectedRows);
    if (name === 'all') {
      submissionsList.map((single_submission) => {
        if (
          value &&
          single_submission.entryStatus === 'complete_entry' &&
          selectedIds.map((e) => e.id).indexOf(single_submission.form_submission_id) === -1
        )
          selectedIds.push({
            id: single_submission.form_submission_id,
            title: formName,
            customFolderId: single_submission.media_custom_folder_id,
          });
        if (!value) selectedIds = [];
        setSelectedRows(selectedIds);
      });
    } else {
      if (value) {
        let customFolderId = '';
        const foundSubmission = submissionsList.find(
          (single_submission) => name === single_submission.form_submission_id,
        );
        if (foundSubmission.entryStatus === 'complete_entry') {
          customFolderId = foundSubmission.media_custom_folder_id;

          selectedIds.push({
            id: name,
            title: formName,
            customFolderId: customFolderId,
          });
        }
      } else selectedIds.splice(selectedIds.map((e) => e.id).indexOf(name), 1);
      setSelectedRows(selectedIds);
    }
  };

  const openSubmissionModal = async (row, currentSectionId) => {
    let supervisorFieldsGlobalIds = [];
    const formSupervisorFields = await CallAPI(
      'GET_SUPERVISOR_FIELDS_OF_FORM_VERSION',
      null,
      row.form_version_id,
      null,
      null,
      null,
    );
    if (formSupervisorFields.status) {
      const elementsToMap = [
        ...formSupervisorFields.data.supervisorElements,
        ...(formSupervisorFields.data.statusElement
          ? [formSupervisorFields.data.statusElement]
          : []),
      ];
      elementsToMap.map((singleSupervisorElement) => {
        supervisorFieldsGlobalIds.push(singleSupervisorElement.elementGlobalId);
      });
    }
    let values = {
      submissionId: row.form_submission_id,
      formVersionId: row.form_version_id,
      mediaCustomFolderId: row.media_custom_folder_id,
      isAnyFieldMissing: false,
      direction: formDirection,
      mediaNotes: row.mediaNotes,
      mediaObj: row.mediaObj,
      elementMetaDataMap: row.elementMetaDataMap,
    };
    let supervisors = [];
    for (let index = 0; index < supervisorList.length; index++) {
      const singleColumn = supervisorList[index];
      if (
        supervisorFieldsGlobalIds.indexOf(singleColumn.name) === -1 &&
        singleColumn.managerSectionGlobalId === currentSectionId
      ) {
        values.isAnyFieldMissing = true;
      }
      if (
        supervisorFieldsGlobalIds.indexOf(singleColumn.name) > -1 &&
        row.submissionFlowState.actionId &&
        row.submissionFlowState.actionId === singleColumn.managerSectionGlobalId &&
        singleColumn.accessLevel === 'full_access'
      ) {
        supervisors.push(singleColumn);

        values[singleColumn.name] =
          row[singleColumn.name] && singleColumn.type !== 'signature' ? row[singleColumn.name] : '';
        if (singleColumn.type === 'signature' && row[singleColumn.name]?.props?.src) {
          const imgBase64 = await imageUrlToBase64(row[singleColumn.name].props.src);
          values[singleColumn.name] = imgBase64 ?? '';
        }
      }
    }

    setEditedSubmissionValues(values);
    setSectionSupervisorList(supervisors);
    setAddSubmission(true);
  };

  const updateSupervisorFields = (row) => {
    if (row.entryStatus === 'complete_entry') {
      if (row.submissionFlowState) {
        if (row.submissionFlowState.flowState === 'section' && row.submissionFlowState.actionId) {
          const allowedSupervisorFieldInCurrentState = supervisorList.find(
            (singleHeaderField) =>
              singleHeaderField.managerSectionGlobalId === row.submissionFlowState.actionId,
          );
          if (allowedSupervisorFieldInCurrentState) {
            if (allowedSupervisorFieldInCurrentState.accessLevel === 'full_access')
              openSubmissionModal(row, allowedSupervisorFieldInCurrentState.managerSectionGlobalId);
            else toast.error('You do not have permission to edit this section');
          } else {
            setShowSupervisorLockedModal(row.submissionFlowState.flowState);
          }
        } else {
          setShowSupervisorLockedModal(row.submissionFlowState.flowState);
        }
      } else {
        setShowSupervisorLockedModal('end');
      }
    }
  };

  const submitDataForm = async (
    formData,
    submissionId,
    versionId,
    managerSectionGlobalId,
    mediaCustomFolderId,
    sendNotificationToInspector,
    updatedFields,
  ) => {
    setSubmissionLoader(true);
    for (let index = 0; index < formData.length; index++) {
      const singleField = formData[index];
      if (singleField.type && singleField.type === 'signature') {
        if (
          signatureMediaIds[submissionId + '_' + singleField.elementGlobalId] &&
          updatedFields?.[singleField.elementGlobalId]
        ) {
          const response = await CallAPI(
            'DELETE_MEDIA',
            {
              folderType: 'submissions',
              mediaId: signatureMediaIds[submissionId + '_' + singleField.elementGlobalId],
              imageName:
                signatureMediaIds[submissionId + '_' + singleField.elementGlobalId] + '.png',
              mediaType: 'image',
              formId: filterData.formId,
              versionId: versionId,
              customFolderId: mediaCustomFolderId,
            },
            null,
            setLoader,
          );
          if (response.status) {
            // delete respective signature key from media
            if (singleField.elementMetaData) {
              singleField.media = singleField.media.filter(
                (item) => item !== singleField.elementMetaData,
              );
              singleField.elementMetaData = '';
            }
          }
        }
        // Upload signature if added
        if (singleField.valueField && updatedFields?.[singleField.elementGlobalId]) {
          let response = await CallAPI(
            'GET_SUBMISSION_MEDIA_UPLOAD_URL',
            {
              imageName:
                signatureStartingText + submissionId + '_' + singleField.elementGlobalId + '.png',
              mediaType: 'image',
              formId: filterData.formId,
              versionId: versionId,
              customFolderId: mediaCustomFolderId,
            },
            null,
            setLoader,
          );
          if (response.status) {
            const blob = await (await fetch(singleField.valueField)).blob();
            await uploadImage(blob, response.data, null);
            singleField.media = [...singleField.media, response.data.id];
            singleField.elementMetaData = JSON.stringify({ signature: response.data.id });
            singleField.valueField = '';
            delete singleField.type;
          }
        } else {
          singleField.valueField = '';
          delete singleField.type;
          if (singleField.elementMetaData) {
            singleField.elementMetaData = JSON.stringify({
              signature: singleField.elementMetaData,
            });
          }
        }
      }
    }

    let result = await CallAPI('UPDATE_SUBMISSION_SUPERVISOR_FIELDS', {
      form: { id: filterData.formId, title: formName },
      formVersion: versionId,
      submissionId: submissionId,
      managerSectionGlobalId,
      locale: lang,
      values: formData,
      sendNotificationToInspector,
    });
    setSubmissionLoader(false);
    if (result.status) {
      toast.success(t('alert_submissions_uploaded_successfully'));
      setAddSubmission(false);
      getFormSubmissions(currentPage, displayEntries);
      setWebReportInfo(null);
    } else {
      toast(result.error, {
        style: { borderRadius: '8px', background: 'red', color: '#fff' },
      });
    }
  };

  const actionsCallBack = (action) => {
    setSelectedRows([]);
    if (action === 'delete') getFormSubmissions();
  };

  const elementCommentCallback = async (row, fieldId, sectionRepetitionNumber) => {
    if (!fieldId) return;
    if (row.entryStatus === 'complete_entry') {
      setCommentsInfo({
        isTestSubmission: filterData.data === 'test-data' ? true : false,
        submissionId: row.form_submission_id,
        formVersionId: row.form_version_id,
        mediaCustomFolderId: row.media_custom_folder_id,
        formTitle: formName,
        formId: filterData.formId,
        submissionCreateByUserId: row.submissionCreateByUserId,
        elementGlobalId: fieldId,
        elementTitle: allQuestionsTitleMap[fieldId] ?? '',
        ...(sectionRepetitionNumber && { sectionRepetitionNumber }),
      });
    }
  };

  const commentCallback = async (row) => {
    if (row.entryStatus === 'complete_entry')
      setCommentsInfo({
        isTestSubmission: filterData.data === 'test-data' ? true : false,
        submissionId: row.form_submission_id,
        formVersionId: row.form_version_id,
        mediaCustomFolderId: row.media_custom_folder_id,
        formTitle: formName,
        formId: filterData.formId,
        submissionCreateByUserId: row.submissionCreateByUserId,
      });
    // const isMessagesRead = row.submissionCommentGroup?.isMessagesRead;
    // console.log({ isMessagesRead });

    // if (isMessagesRead === false) {
    //   console.log('calling');

    //   const result = await CallAPI('UPDATE_SUBMISSION_COMMENT_READ_STATUS', {
    //     submissionId: row.form_submission_id,
    //     commentGroupUserId: row.submissionCommentGroup._id,
    //   });
    //   if (result.status) {
    //     getFormSubmissions(currentPage, displayEntries);
    //     toast.success('Record status updated successfully');
    //   } else {
    //     toast.error(result.error);
    //   }
    // }
  };

  const gotoRecordPreviewPage = async (row) => {
    const formSubmissionId = row.form_submission_id;
    if (row.entryStatus === 'complete_entry')
      setWebReportInfo({
        formId: filterData.formId,
        submissionId: formSubmissionId,
        row,
        dataType: filterData?.data === 'live-data' ? 'real' : 'test',
        allowEditManagerSection:
          supervisorList.filter((field) => field.type !== 'status').length > 0,
      });
  };

  const preViewLogs = (id) => {
    setLogsSubmissionId(id);
  };

  useEffect(() => {
    if (selectedRows.length === 1) {
      const foundSubmission = submissionsList.find(
        (sub) => sub.form_submission_id === selectedRows[0].id,
      );
      setWebPagePayloadDetails((prev) => ({
        ...prev,
        referenceNumber: foundSubmission.referenceNumber,
        submissionDate: foundSubmission.createdAtTime,
        userName: `${foundSubmission.firstName} ${foundSubmission.lastName}`,
      }));
    }
  }, [selectedRows]);

  return (
    <div className='theme-background-white-1'>
      <div className='d-flex gap-3 align-items-center px-4 pt-3'>
        <img src={formGreenIcon} className='svg-black' />
        <label className='theme-font-inter-medium theme-text-black-1 theme-size-1_1'>
          {formName}
        </label>
        {dataSourceFilters.map((filter, index) => (
          <SelectField
            key={index}
            label={
              dataSourceFilterData[filter.name] && dataSourceFilterData[filter.name] !== ''
                ? dataSourceFilterData[filter.name]
                : dataSourceFilterData[filter.name] === ''
                ? 'select_show_all'
                : filter.displayName
            }
            listValues={filter.options}
            icon={arrowDownIcon}
            handleChange={handleDataSourceFilterDataChange}
            selectedValues={[]}
            name={filter.name}
            isAutoClose={true}
            variant='white-1'
            classes='width-max-content'
            menuClasses={'width-max-content'}
          />
        ))}
      </div>
      <SubmissionFilters
        allQuestions={allQuestions}
        versionsList={versionsList}
        filterData={filterData}
        setFilterData={setFilterData}
        showAttachment={showAttachment}
        setShowAttachment={setShowAttachment}
        queryFilter={queryFilter}
        setQueryFilter={setQueryFilter}
        searchState={searchState}
        handleSearchStateChange={handleSearchStateChange}
        selectedRows={selectedRows}
        actionsCallBack={actionsCallBack}
        submissionsRowsCount={submissionsList.length}
        webPagePayloadDetails={webPagePayloadDetails}
      />

      <div className='submissions'>
        <TableComponent
          data={submissionsList}
          header={submissionHeaderList}
          props={{
            type: 'submissions',
            mediaEnabled: showAttachment === 'attachment',
            actionsType: 'dashboardForms',
            gotoRecordPreviewPage,
            commentCallback,
            preViewLogs,
            translateValueFunc: (val) => t(val),
          }}
          totalRecords={totalRecords}
          filterEnabled={false}
          loader={loader}
          isFieldSelectionEnabled={false}
          isPaginationEnabled={false}
          isActionsEnabled={false}
          isSelectionEnabled={true}
          selectionKey='form_submission_id'
          onSelection={onRowsSelection}
          selectedRows={selectedRows}
          maintainTableMaxHeight
        />
      </div>
      {totalRecords && currentPage ? (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          displayEntries={displayEntries}
          setDisplayEntries={changeDisplayEntries}
          currentPageRecords={submissionsList ? submissionsList.length : 0}
          paginationFunction={getFormSubmissions}
        />
      ) : null}

      {addSubmission && (
        <AddSubmission
          supervisorList={sectionSupervisorList}
          onSubmitForm={submitDataForm}
          loading={submissionLoader}
          values={editedSubmissionValues}
          handleClose={() => {
            setAddSubmission(false);
          }}
          isNestedModal={!!webReportInfo}
          formName={formName}
        />
      )}
      {logsSubmissionId && (
        <SubmissionLogs submissionId={logsSubmissionId} setSubmissionId={setLogsSubmissionId} />
      )}

      {webReportInfo && (
        <ReportModal
          webReportInfo={webReportInfo}
          setWebReportInfo={setWebReportInfo}
          editManagerSection={updateSupervisorFields}
          commentCallback={commentCallback}
          elementCommentCallback={elementCommentCallback}
        />
      )}
      {commentsInfo && (
        <AddCommentModal
          commentsInfo={commentsInfo}
          closeModal={() => setCommentsInfo(null)}
          currentUserId={currentUserId}
          isNestedModal={!!webReportInfo}
        />
      )}
      {showSupervisorLockedModal && (
        <SupervisorLockedModal
          showSupervisorLockedModal={showSupervisorLockedModal}
          setShowSupervisorLockedModal={setShowSupervisorLockedModal}
          isNestedModal={!!webReportInfo}
        />
      )}
    </div>
  );
}

export default Submissions;
